import type { User, UserManagerSettings } from 'oidc-client-ts'
import type { Ref } from 'vue'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { ref } from 'vue'
import { clearBirtaBrowserState } from '@/features/birta-session/browserStorage'

export enum AuthenticationMethod {
  electronicID = 'mobileid',
}

interface BirtaOidcProfile {
  id?: string
  sub: string
  name?: string
  email?: string
  locale?: string
  amr?: AuthenticationMethod | AuthenticationMethod[]
  phone_number?: string
}

export interface BirtaUserProfile {
  /** Icelandic national ID (kennitala). */
  id: string
  name: string | undefined
  email: string | undefined
  locale: string | undefined
  phoneNumber: string | undefined
  userId: string | undefined
  amr: AuthenticationMethod[]
}

const {
  VITE_AUTHORITY,
  VITE_CLIENT_ID,
  VITE_REDIRECT_URI,
  VITE_POST_LOGOUT_REDIRECT_URI,
  VITE_SILENT_REDIRECT_URI,
  VITE_PUBLIC_PATH,
} = import.meta.env

const config: UserManagerSettings = {
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  authority: VITE_AUTHORITY,
  client_id: VITE_CLIENT_ID,
  redirect_uri: VITE_REDIRECT_URI,
  silent_redirect_uri:
    VITE_SILENT_REDIRECT_URI
    || `${window.location.origin}${VITE_PUBLIC_PATH ?? '/'}auth/silent-renew.html`,
  post_logout_redirect_uri: VITE_POST_LOGOUT_REDIRECT_URI,
  filterProtocolClaims: false,
  includeIdTokenInSilentRenew: true,
  response_type: 'code',
  scope:
    'openid profile customerclientportalsaleapiscope customerclientportalpaymentscope customerclientportalcustomersscope customerclientportaladvisorscope',

  // Renewal is managed explicitly so token events and HTTP 401 recovery
  // use the same shared silent-renew operation.
  automaticSilentRenew: false,
}

/**
 * Exported for the silent-renew callback entry point.
 */
export const manager = new UserManager(config)

const normalizeAuthenticationMethods = (
  authenticationMethods: BirtaOidcProfile['amr'],
): AuthenticationMethod[] => {
  if (Array.isArray(authenticationMethods)) {
    return authenticationMethods
  }

  if (authenticationMethods) {
    return [authenticationMethods]
  }

  return []
}

const buildBirtaCanonicalReturnPath = (): string => {
  const normalizedBasePath = (VITE_PUBLIC_PATH ?? '/').replace(/\/$/, '')
  return `${normalizedBasePath}/yfirlit`
}

class AuthManager {
  token: string = ''
  loggedIn: boolean = false
  locale: string | null = null
  userProfile: BirtaUserProfile | null = null
  userProfileRef: Ref<BirtaUserProfile | null> = ref(null)

  private expiresAt: number | null = null
  private silentRenewPromise: Promise<boolean> | null = null
  private interactiveAuthenticationPromise: Promise<void> | null = null

  /**
   * Used by the boot gate to decide whether the stored token should be renewed.
   */
  get isNearExpiry(): boolean {
    if (!this.loggedIn || this.expiresAt === null) {
      return false
    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000)
    const secondsUntilExpiry = this.expiresAt - currentTimeInSeconds

    return secondsUntilExpiry < 120
  }

  /**
   * Redirect to IdentityServer and preserve the requested return path.
   */
  beginAuthentication(returnPath?: string): Promise<void> {
    if (this.interactiveAuthenticationPromise) {
      return this.interactiveAuthenticationPromise
    }

    const fallbackPath = window.location.pathname + window.location.search
    window.sessionStorage.setItem('oidc:nextUrl', returnPath ?? fallbackPath)

    this.interactiveAuthenticationPromise = (async () => {
      try {
        await manager.signinRedirect()
      }
      catch (error) {
        window.sessionStorage.removeItem('oidc:nextUrl')
        throw error
      }
    })().catch((error: unknown) => {
      this.interactiveAuthenticationPromise = null
      throw error
    })

    return this.interactiveAuthenticationPromise
  }

  /**
   * Authentication-recovery entry point. The Birta bootstrap flow determines
   * the correct destination after the authenticated identity is known.
   */
  beginAuthenticationToBirtaEntry(): Promise<void> {
    return this.beginAuthentication(buildBirtaCanonicalReturnPath())
  }

  /**
   * Intentional sign-out clears resumable Birta browser state.
   * Authentication recovery does not use this method.
   */
  logout(): void {
    clearBirtaBrowserState()

    void manager.signoutRedirect({
      post_logout_redirect_uri: VITE_POST_LOGOUT_REDIRECT_URI,
    })
  }

  private setUser(user: User | null): void {
    if (user) {
      const profile = user.profile as BirtaOidcProfile

      this.loggedIn = true
      this.token = user.access_token
      this.locale = profile.locale ?? null
      this.expiresAt = user.expires_at ?? null
      this.userProfile = {
        id: profile.id ?? profile.sub,
        name: profile.name,
        email: profile.email,
        locale: profile.locale,
        phoneNumber: profile.phone_number,
        userId: profile.sub,
        amr: normalizeAuthenticationMethods(profile.amr),
      }
    }
    else {
      this.loggedIn = false
      this.token = ''
      this.locale = null
      this.expiresAt = null
      this.userProfile = null
    }

    this.userProfileRef.value = this.userProfile
  }

  /**
   * Read the current OIDC user from session storage.
   */
  async getUser(): Promise<void> {
    const user = await manager.getUser()
    this.setUser(user)
  }

  /**
   * Silently renew the access token. Concurrent callers await the same operation.
   * Returns whether a new authenticated user/token was obtained.
   */
  trySignInSilent(): Promise<boolean> {
    if (this.silentRenewPromise) {
      return this.silentRenewPromise
    }

    this.silentRenewPromise = this.performSilentRenew().finally(() => {
      this.silentRenewPromise = null
    })

    return this.silentRenewPromise
  }

  async signInSilent(): Promise<void> {
    await this.trySignInSilent()
  }

  private async performSilentRenew(): Promise<boolean> {
    try {
      const user = await manager.signinSilent()
      this.setUser(user)
      return true
    }
    catch {
      return false
    }
  }

  private async handleAccessTokenExpired(): Promise<void> {
    // A renewal may have completed just before the expiry event was delivered.
    if (this.loggedIn && !this.isNearExpiry) {
      return
    }

    if (this.silentRenewPromise) {
      const didRenewToken = await this.silentRenewPromise

      if (didRenewToken) {
        return
      }
    }

    await this.beginAuthenticationToBirtaEntry()
  }

  /**
   * Register oidc-client-ts lifecycle events.
   *
   * Birta currently renews without activity gating. Revisit this if Birta
   * introduces an inactivity timeout or session-expiry modal.
   */
  registerEvents(): void {
    manager.events.addAccessTokenExpiring(() => {
      void this.signInSilent()
    })

    manager.events.addAccessTokenExpired(() => {
      void this.handleAccessTokenExpired()
    })

    manager.events.addUserLoaded((user: User) => {
      this.setUser(user)
    })

    manager.events.addUserUnloaded(() => {
      this.setUser(null)
    })
  }

  /**
   * Complete the IdentityServer callback and restore the saved Birta path.
   */
  async completeAuthentication(): Promise<void> {
    try {
      const user = await manager.signinRedirectCallback()
      this.setUser(user)
    }
    catch (error) {
      // /callback can be re-entered (browser refresh/back) after the OIDC state was
      // already consumed by an earlier, successful run of this same callback. Recover
      // from the persisted user instead of surfacing the raw oidc-client-ts error.
      const existingUser = await manager.getUser()
      if (!existingUser || existingUser.expired) {
        throw error
      }
      this.setUser(existingUser)
    }

    // replace(), not href=, so the /callback?code&state URL never lands in
    // browser history — otherwise Back reloads it with an already-consumed state.
    window.location.replace(
      this.getAndRemoveReturnPath()
      || (VITE_PUBLIC_PATH ?? '/'),
    )
  }

  private getAndRemoveReturnPath(): string | null {
    const returnPath = window.sessionStorage.getItem('oidc:nextUrl')
    window.sessionStorage.removeItem('oidc:nextUrl')

    return returnPath
  }
}

const auth = new AuthManager()

export default auth
